import { createSelector } from "@reduxjs/toolkit";
import { useThrottleEffect } from "Hooks/UseThrottle";

import { IState } from "Model/State/IState";
import { IProject } from "Model/Storage/IProject";
import { useCallback, useState } from "react";
import { useSelector } from "react-redux";
import { selectFilterIdList } from "Store/Selectors/Filter";
import { selectProjectMap, selectProjectsHash, selectSortedProjectIds } from "Store/Selectors/Projects";
import { hasNoElements } from "Utils/Array";
import { isEmpty } from "Utils/Object";
import { IUseProjects } from "./IUseProjects";
import { IUseProjectsState } from "./IUseProjectsState";

export function useProjects(): IUseProjects {
  const {
    projectMap, projectListHash, sortedProjectIdList, filteredProjectIdList
  } = useSelector<
  IState,
  IUseProjectsState
  >(
    createSelector(
      selectProjectMap,
      selectProjectsHash,
      selectSortedProjectIds,
      selectFilterIdList,
      (map, hash, sortedIdList, filteredIdList) => ({
        projectMap: map,
        projectListHash: hash,
        sortedProjectIdList: sortedIdList,
        filteredProjectIdList: filteredIdList
      })
    )
  );

  const [projectList, setProjectList] = useState<IProject[]>([]);
  const generateProjectList = useCallback(
    (map: Record<string, IProject>) => {
      setProjectList(sortProjectList(filterProjectList(map, filteredProjectIdList), sortedProjectIdList));
    },
    [filteredProjectIdList, sortedProjectIdList]
  );

  // this effect is used to fast render the first resultset and then
  // wait for all remaining results to come in and then do a hopefully™️
  // final filter and sort run through the whole project list.
  useThrottleEffect(() => generateProjectList(projectMap), 250, [generateProjectList, projectMap]);

  return {
    projectList,
    projectListHash
  };
}

function filterProjectList(
  projectMap: Record<string, IProject>,
  filteredProjectIdList: string[]
): Record<string, IProject> {
  if (isEmpty(projectMap) || hasNoElements(filteredProjectIdList)) {
    return {};
  }

  const filteredProjectMap = {};

  for (const id of filteredProjectIdList) {
    filteredProjectMap[id] = projectMap[id];
  }

  return filteredProjectMap;
}

function sortProjectList(projectMap: Record<string, IProject>, sortedProjectIdList: string[]): IProject[] {
  if (hasNoElements(sortedProjectIdList)) {
    return Object.values(projectMap);
  }

  const sortedProjectList = [];
  for (const id of sortedProjectIdList) {
    if (projectMap[id] == null) {
      continue;
    }

    sortedProjectList.push(projectMap[id]);
  }

  return sortedProjectList;
}
